import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { SegmentOption } from '../../segmented-control/segmented-control.component';
import { CreatorLibraryService, creatorFailure, TenantKit } from '../../../services/creator-library.service';

interface LookOption { id: string; label: string; hint: string; }

/**
 * „Kurs von Hand bauen" (Creator › Neuer Kurs): an E-Learning or Microlearning
 * course from the course editor's starter template. The server creates it with
 * its first revision (`POST /creator/items`, sourceKind `blank` → `/v1/blank`
 * → `/v1/validate`); afterwards the course opens in the course editor
 * (`/creator/workspace?item=…&kind=…`).
 *
 * Looks: the two built-in product kits, shown as „Classic" (`classic`) and
 * „Rounded" (`standard`, the default) — names by Marcel, 28.10.2026 — plus the
 * organisation's own Brand Kits.
 */
@Component({
  selector: 'app-creator-ac-course-dialog',
  templateUrl: './creator-ac-course-dialog.component.html',
  styleUrl: './creator-ac-course-dialog.component.scss',
})
export class CreatorAcCourseDialogComponent implements OnInit {
  readonly form = new FormGroup({
    kind: new FormControl<string>('elearning_course', { nonNullable: true }),
    title: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(200)] }),
    kitId: new FormControl<string>('standard', { nonNullable: true }),
    lang: new FormControl<string>('de', { nonNullable: true }),
    voice: new FormControl<'du' | 'sie'>('du', { nonNullable: true }),
  });

  readonly kinds: SegmentOption[];
  readonly looks: LookOption[];
  ownKits: TenantKit[] = [];
  saving = false;

  constructor(
    private ref: NbDialogRef<CreatorAcCourseDialogComponent>,
    private library: CreatorLibraryService,
    private router: Router,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {
    this.kinds = [
      { value: 'elearning_course', label: t.instant('pages.creator.kinds.elearning_course') },
      { value: 'microlearning', label: t.instant('pages.creator.kinds.microlearning') },
    ];
    this.looks = [
      { id: 'classic', label: 'Classic', hint: t.instant('pages.creator.acDialog.classicHint') },
      { id: 'standard', label: 'Rounded', hint: t.instant('pages.creator.acDialog.roundedHint') },
    ];
    if (t.currentLang === 'en') this.form.controls.lang.setValue('en');
  }

  ngOnInit(): void {
    // without the list the built-in looks still work
    this.library.kits().subscribe({ next: (kits) => (this.ownKits = kits), error: () => (this.ownKits = []) });
  }

  pickLook(id: string): void {
    this.form.controls.kitId.setValue(id);
  }

  cancel(): void {
    this.ref.close();
  }

  create(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;
    const value = this.form.getRawValue();
    const title = value.title.trim();
    if (!title) {
      this.form.controls.title.setErrors({ required: true });
      return;
    }
    this.saving = true;
    this.library
      .createAc({ kind: value.kind, title, kitId: value.kitId, lang: value.lang, voice: value.voice })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: ({ id }) => {
          this.ref.close();
          this.router.navigate(['/creator/workspace'], { queryParams: { item: id, kind: value.kind, title } });
        },
        error: (err) => {
          const failure = creatorFailure(err);
          this.toastr.danger(failure.message || this.t.instant('pages.creator.acDialog.failed'), this.t.instant('pages.creator.acDialog.failed'));
        },
      });
  }
}
